import React, { useState, useEffect, useRef, useMemo } from "react";
import api from "../services/api";
import useProjectStore, { NO_PROJECT } from "../store/useProjectStore";
import { useBreadcrumb } from "../hooks/useBreadcrumb";
import Spinner from "../shared/shared-component/Spinner";

const STATUS_CONFIG = {
  completed: { cls: "badge__success", label: "Completed" },
  failed: { cls: "badge__danger", label: "Failed" },
  running: { cls: "badg__info", label: "Running" },
  queued: { cls: "badge__warning", label: "Queued" },
};

const getStatusBadge = (status) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.running;
  return (
    <span className={`status__badge ${c.cls}`}>
      <span className={`badgeDot ${c.cls}`}></span>
      {c.label}
    </span>
  );
};

const formatDuration = (ms) => {
  if (!ms) return "—";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
};

const progressPercentage = (execution) => {
  if (!execution.progress || execution.progress.total === 0) return 0;
  return Math.round(
    (execution.progress.completed / execution.progress.total) * 100,
  );
};

const ProjectBadge = ({ projectName }) => {
  if (!projectName) return null;
  return (
    <span className="wrapper__backlight__10__600">
      <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
        <path
          d="M1.5 3.5A1 1 0 012.5 2.5h4l1.5 2H13.5a1 1 0 011 1v7a1 1 0 01-1 1h-11a1 1 0 01-1-1v-9z"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
        />
      </svg>
      {projectName}
    </span>
  );
};

const InputsCell = ({ inputs }) => (
  <span className="fb__12 fw__700 fc__5F636A ff__jetBrainsMono bg__F5F3F0 py__5 br__4">
    {inputs && Object.keys(inputs).length > 0 ? (
      Object.entries(inputs)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => (
          <span key={k} className="ff__jetBrainsMono">
            <span className="fc__5F636A">{k}: </span>
            {String(v)}
          </span>
        ))
    ) : (
      <span style={{ color: "var(--text-muted)" }}>—</span>
    )}
  </span>
);

const DateCell = ({ date }) => (
  <>
    {new Date(date).toLocaleDateString()}{" "}
    <span style={{ color: "var(--text-muted)" }}>
      {new Date(date).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}
    </span>
  </>
);

const RowActions = ({
  execution,
  expandedId,
  setExpandedId,
  onOpenExecution,
  handleDelete,
}) => (
  <div className="exec-actions">
    <button
      className="history__btn open"
      onClick={() => onOpenExecution?.(execution)}
    >
      Open
    </button>
    {/* <button
      className="history__btn view"
      onClick={() =>
        setExpandedId(expandedId === execution.id ? null : execution.id)
      }
    >
      {expandedId === execution.id ? "Hide" : "View"}
    </button> */}
    <button
      className="history__btn delete"
      onClick={() => handleDelete(execution.id)}
    >
      Delete
    </button>
  </div>
);

// useDebouncedValue — small reusable hook.
function useDebouncedValue(value, delay = 400) {
  const [debouned, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delay);

    return clearTimeout(() => timer);
  }, [value, delay]);

  return debouned;
}

// StatusFilterDropdown — same open/close

const STATUS_FILTER_OPTIONS = [
  { id: null, label: "All" },
  { id: "completed", label: "Completed" },
  { id: "running", label: "Running" },
  { id: "queued", label: "Queued" },
  { id: "failed", label: "Failed" },
];

function StatusFilterDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectedLabel =
    STATUS_FILTER_OPTIONS.find((opt) => opt.id === value)?.label || "All";
  return (
    <div ref={ref} className="wrapper___status__filter pos__rel">
      <button
        className="status__filter__btn fb__13 fw__700 fc__5F636A"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="statusIcon">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_669_5846)">
              <path
                d="M5.83247 11.6667C5.83242 11.7751 5.86258 11.8813 5.91956 11.9736C5.97654 12.0658 6.05809 12.1403 6.15506 12.1887L7.32172 12.7721C7.41068 12.8165 7.50952 12.8375 7.60886 12.833C7.70821 12.8285 7.80475 12.7987 7.88932 12.7464C7.9739 12.6941 8.04369 12.621 8.09208 12.5341C8.14047 12.4472 8.16585 12.3494 8.16581 12.25V8.16667C8.16594 7.87756 8.27341 7.59879 8.46739 7.38442L12.6808 2.72417C12.7563 2.64049 12.806 2.53673 12.8238 2.42543C12.8416 2.31413 12.8267 2.20006 12.7811 2.09701C12.7354 1.99396 12.6609 1.90635 12.5665 1.84476C12.4721 1.78318 12.3619 1.75027 12.2491 1.75H1.74914C1.63633 1.75004 1.52595 1.78279 1.43137 1.84429C1.33679 1.90578 1.26207 1.99338 1.21627 2.09648C1.17046 2.19957 1.15553 2.31374 1.17329 2.42514C1.19104 2.53655 1.24072 2.64042 1.31631 2.72417L5.53089 7.38442C5.72487 7.59879 5.83234 7.87756 5.83247 8.16667V11.6667Z"
                stroke="#5F636A"
                strokeWidth="1.16667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_669_5846">
                <rect width="14" height="14" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </span>
        Status: {selectedLabel}
      </button>
      {open && (
        <div className="wrapper__s__Filter___dropdown">
          <ul>
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <li
                className={value === opt.id ? "s__Filter__active" : "s__Filter"}
                key={opt.id ?? "All"}
                onClick={() => {
                  onChange(opt.id);
                  setOpen(false);
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main component
// ============================================================

export default function ExecutionHistory({
  agents,
  onOpenExecution,
  projects = [],
}) {
  const [executions, setExecutions] = useState([]);
  const [children, setChildren] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedParentId, setExpandedParentId] = useState(null);
  // Which project the list is filtered to, chosen in the header
  // (ProjectSwitcher): null means "All Projects" — still the default on every
  // visit — and NO_PROJECT means runs that belong to no project.
  const filterProjectId = useProjectStore((s) => s.historyProjectId);
  const setFilterProjectId = useProjectStore((s) => s.setHistoryProjectId);
  const [searchTerm, setSearchTerms] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  useBreadcrumb(
    [{ label: "Workspace", to: "/mainScreen" }, { label: "Execution History" }],
    [],
  );

  // Leaving the screen drops the filter, so History always opens on every
  // run — the behaviour it had when the dropdown lived on the page.
  useEffect(() => {
    return () => setFilterProjectId(null);
  }, [setFilterProjectId]);

  useEffect(() => {
    loadExecutions(filterProjectId);
  }, [filterProjectId]);

  const loadExecutions = async (projectId) => {
    try {
      setLoading(true);
      const response = await api.getExecutions(100, projectId);
      setExecutions(response.data.executions);

      // Fire all child-job requests together instead of one-by-one —
      // like sending multiple fetch() calls at once instead of awaiting
      // each one before starting the next.
      const parents = response.data.executions.filter(
        (exec) => exec.jobType === "parent" && exec.childJobIds?.length > 0,
      );

      const results = await Promise.all(
        parents.map((exec) =>
          api
            .getExecutionChildren(exec.id)
            .then((res) => ({ id: exec.id, children: res.data.children || [] }))
            .catch((err) => {
              console.error(
                `Error loading children for parent ${exec.id}:`,
                err,
              );
              return { id: exec.id, children: [] };
            }),
        ),
      );

      const childrenMap = {};
      results.forEach(({ id, children }) => {
        childrenMap[id] = children;
      });
      setChildren(childrenMap);
    } catch (err) {
      console.error("Error loading executions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Delete this execution?")) return;

    try {
      await api.deleteExecution(jobId);
      setExecutions(executions.filter((e) => e.id !== jobId));
    } catch (err) {
      console.error("Error deleting execution:", err);
    }
  };

  // filtering logic
  const filteredExecutions = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase();

    return executions.filter((exec) => {
      if (exec.jobType === "child") return true;

      if (statusFilter && exec.status !== statusFilter) return false;

      if (!term) return true;

      const searchableFields = [
        exec.agentName,
        exec.subcommand,
        exec.projectName,
        exec.command,
        ...(exec.inputs ? Object.values(exec.inputs).map(String) : []),
      ].filter(Boolean);

      return searchableFields.some((field) =>
        field.toLowerCase().includes(term),
      );
    });
  }, [executions, statusFilter, debouncedSearchTerm]);

  const renderParentJob = (execution) => {
    const isExpanded = expandedParentId === execution.id;
    const progress = execution.progress || {
      total: 0,
      completed: 0,
      failed: 0,
    };
    const percent = progressPercentage(execution);
    const childrenList = children[execution.id] || [];

    return (
      <React.Fragment key={execution.id}>
        <tr>
          <td>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() =>
                  setExpandedParentId(isExpanded ? null : execution.id)
                }
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  padding: "0",
                  color: "var(--text-secondary)",
                }}
              >
                {isExpanded ? "▼" : "▶"}
              </button>
              <span>📦 {execution.agentName}</span>
              {execution.subcommand && (
                <>
                  <span style={{ color: "var(--text-muted)", margin: "0 5px" }}>
                    ›
                  </span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {execution.subcommand}
                  </span>
                </>
              )}
              <ProjectBadge projectName={execution.projectName} />
            </div>
          </td>
          <td className="table-col-hide">
            <InputsCell inputs={execution.inputs} />
          </td>
          <td>{getStatusBadge(execution.status)}</td>
          <td
            className="table-col-hide"
            style={{ fontSize: "12px", color: "var(--text-secondary)" }}
          >
            <DateCell date={execution.createdAt} />
          </td>
          <td className="table-col-hide">
            <div
              style={{
                fontSize: "12px",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              <div
                style={{ marginBottom: "6px", color: "var(--text-secondary)" }}
              >
                {progress.completed}/{progress.total}
              </div>
              <div
                style={{
                  width: "60px",
                  height: "6px",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${percent}%`,
                    height: "100%",
                    background: "linear-gradient(90deg, #06b6d4, #10b981)",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          </td>
          <td>
            <RowActions
              execution={execution}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              onOpenExecution={onOpenExecution}
              handleDelete={handleDelete}
            />
          </td>
        </tr>

        {expandedId === execution.id && (
          <tr>
            <td colSpan="6" style={{ padding: "0" }}>
              <div
                style={{
                  background: "rgba(6,182,212,0.025)",
                  borderTop: "1px solid var(--border)",
                  padding: "20px 24px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: "14px",
                  }}
                >
                  Parent Job Details
                </div>
                <table
                  className="table"
                  style={{ fontSize: "12px", marginBottom: "20px" }}
                >
                  <tbody>
                    <tr>
                      <td
                        style={{
                          fontWeight: "600",
                          width: "140px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Job ID
                      </td>
                      <td
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: "11px",
                          color: "var(--text-muted)",
                        }}
                      >
                        {execution.id}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "600",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Status
                      </td>
                      <td>{getStatusBadge(execution.status)}</td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "600",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Subagents
                      </td>
                      <td style={{ color: "var(--text-secondary)" }}>
                        {progress.completed}/{progress.total} completed
                      </td>
                    </tr>
                    {execution.projectName && (
                      <tr>
                        <td
                          style={{
                            fontWeight: "600",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Project
                        </td>
                        <td>
                          <ProjectBadge projectName={execution.projectName} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        )}

        {isExpanded &&
          childrenList.length > 0 &&
          childrenList.map((child) => (
            <tr
              key={child.id}
              style={{
                background: "rgba(6,182,212,0.03)",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <td
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  paddingLeft: "60px",
                }}
              >
                <span>└─ {child.agentName}</span>
              </td>
              <td className="table-col-hide">
                <InputsCell inputs={child.inputs} />
              </td>
              <td>{getStatusBadge(child.status)}</td>
              <td
                className="table-col-hide"
                style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
                <DateCell date={child.createdAt} />
              </td>
              <td
                className="table-col-hide"
                style={{
                  fontSize: "12px",
                  color: "var(--text-secondary)",
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {formatDuration(child.duration)}
              </td>
              <td>
                <button
                  className="btn btn-small"
                  onClick={() => onOpenExecution?.(child)}
                  style={{
                    background: "rgba(16,185,129,0.1)",
                    border: "1px solid rgba(16,185,129,0.25)",
                    color: "#34d399",
                    padding: "4px 10px",
                    fontSize: "11px",
                  }}
                >
                  Open
                </button>
              </td>
            </tr>
          ))}
      </React.Fragment>
    );
  };

  const renderStandaloneJob = (execution) => {
    return (
      <React.Fragment key={execution.id}>
        <tr>
          <td>
            <div className="wrapper__agent__td d-flex flex-wrap">
              <span className="fc__0E1218 fb__16 fw__700">
                {execution.agentName}
              </span>
              {execution.subcommand && (
                <>
                  <span className="fc__5F636A mlr__5">›</span>
                  <span className="fc__5F636A">{execution.subcommand}</span>
                </>
              )}
              <ProjectBadge projectName={execution.projectName} />
            </div>
          </td>
          <td className="table-col-hide">
            <InputsCell inputs={execution.inputs} />
          </td>
          <td>{getStatusBadge(execution.status)}</td>
          <td
            className="table-col-hide"
            style={{ fontSize: "12px", color: "var(--text-secondary)" }}
          >
            <DateCell date={execution.createdAt} />
          </td>
          <td
            className="table-col-hide"
            style={{
              fontSize: "12px",
              color: "var(--text-secondary)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {formatDuration(execution.duration)}
          </td>
          <td>
            <RowActions
              execution={execution}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              onOpenExecution={onOpenExecution}
              handleDelete={handleDelete}
            />
          </td>
        </tr>

        {expandedId === execution.id && (
          <tr>
            <td colSpan="6" style={{ padding: "0" }}>
              <div
                style={{
                  background: "rgba(6,182,212,0.025)",
                  borderTop: "1px solid var(--border)",
                  padding: "20px 24px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: "14px",
                  }}
                >
                  Execution Details
                </div>
                <table className="table" style={{ fontSize: "12px" }}>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          fontWeight: "600",
                          width: "140px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Job ID
                      </td>
                      <td
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: "11px",
                          color: "var(--text-muted)",
                        }}
                      >
                        {execution.id}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "600",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Full Command
                      </td>
                      <td>
                        <code
                          style={{
                            background: "rgba(6,182,212,0.07)",
                            border: "1px solid rgba(6,182,212,0.14)",
                            color: "var(--teal-bright)",
                            padding: "2px 8px",
                            borderRadius: "5px",
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "11px",
                          }}
                        >
                          /{execution.command}
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "600",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Status
                      </td>
                      <td>{getStatusBadge(execution.status)}</td>
                    </tr>
                    {execution.projectName && (
                      <tr>
                        <td
                          style={{
                            fontWeight: "600",
                            color: "var(--text-secondary)",
                          }}
                        >
                          Project
                        </td>
                        <td>
                          <ProjectBadge projectName={execution.projectName} />
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td
                        style={{
                          fontWeight: "600",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Started
                      </td>
                      <td style={{ color: "var(--text-secondary)" }}>
                        {execution.startedAt
                          ? new Date(execution.startedAt).toLocaleString()
                          : "Not started"}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "600",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Completed
                      </td>
                      <td style={{ color: "var(--text-secondary)" }}>
                        {execution.completedAt
                          ? new Date(execution.completedAt).toLocaleString()
                          : "In progress"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return <Spinner text="Loading history..." />;
  }

  const filterLabel =
    filterProjectId === NO_PROJECT
      ? "No Project"
      : filterProjectId
        ? projects.find((p) => p.id === filterProjectId)?.name || "Project"
        : null;

  return (
    <section className="wrapper__execution__history wrapper__main">
      <div className="wrapper__history__top d-flex align-items-center justify-content-between mb__32">
        <div className="head">
          <h2 className="h2 mb__6 fc__0E1218 fw__700">Execution History</h2>
          <p className="fb__16 fc__5F636A">
            Audit trail across every agent run in this workspace.
          </p>
        </div>
        <div className="wrapper__history__filter d-flex align-items-center">
          <div className="input__filter">
            <input
              value={searchTerm}
              type="text"
              placeholder="Filter runs…"
              className="fb__13 fw__700 fc__5F636A"
              onChange={(e) => setSearchTerms(e.target.value)}
            />
            <span className="serachIcon">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.2504 12.2504L9.71875 9.71875"
                  stroke="#5F636A"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.41667 11.0833C8.994 11.0833 11.0833 8.994 11.0833 6.41667C11.0833 3.83934 8.994 1.75 6.41667 1.75C3.83934 1.75 1.75 3.83934 1.75 6.41667C1.75 8.994 3.83934 11.0833 6.41667 11.0833Z"
                  stroke="#5F636A"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
          <StatusFilterDropdown
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
      </div>

      <div className="wrapper__execution__history__inner">
        <div className="wrapper__recents__head d-flex align-items-center justify-content-between mb__24">
          <div className="recent__left d-flex align-items-center">
            <span className="fb__13 fw__600 fc__0E1218">Recent Runs</span>
            {filterLabel && (
              <span className="fb__12 recent__label">{filterLabel}</span>
            )}
            <span className="fb__12 fw__400 fc__5F636A">
              {filteredExecutions.filter((e) => e.jobType !== "child").length}{" "}
              runs
            </span>
          </div>
        </div>

        {filteredExecutions.length === 0 ? (
          <div className="empty__state">
            <div className="empty__state__title">
              {filterProjectId
                ? "No executions in this project"
                : "No executions yet"}
            </div>
            <div className="empty__state__desc">
              {filterProjectId
                ? "Try selecting a different project or 'All Projects'"
                : "Run a skill to see its execution history here"}
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th className="table-col-hide">Command</th>
                  <th>Status</th>
                  <th className="table-col-hide">Created</th>
                  <th className="table-col-hide">Duration</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExecutions.map((execution) => {
                  if (execution.jobType === "child") return null;
                  if (execution.jobType === "parent")
                    return renderParentJob(execution);
                  return renderStandaloneJob(execution);
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
